/**
 * Notification query options — Layer 2 of the three-layer TanStack pattern.
 *
 * Reusable, typed query definitions for the notification center.
 * Consumed by Layer 3 hooks in useNotifications.ts.
 */
import { queryOptions, keepPreviousData } from "@tanstack/react-query";
import {
  notificationCenter,
  notificationHasUnread,
  notificationInbox,
  notificationMarkRead,
  notificationMarkAllRead,
} from "./notificationApi";
import { notificationKeys } from "@/shared/lib/queryKeys";

/** Query options for the notification center (inbox + has_unread combined). */
export function notificationCenterQueryOptions() {
  return queryOptions({
    queryKey: notificationKeys.list(),
    queryFn: notificationCenter,
    // staleTime: Infinity prevents any background refetch from causing a badge flicker.
    // Data is refetched only when:
    //   - SSE stream sends a notification event (useNotificationStream invalidates)
    //   - mark-read/mark-all-read mutation settles (onSettled invalidates)
    // placeholderData: keepPreviousData preserves the last known badge count during refetch —
    // this is what keeps the red dot stable across navigations and StrictMode remounts.
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });
}

/** Query options for bell badge — lightweight has_unread check.
 * Polls every 30s so the red dot stays in sync with the server.
 */
export function notificationInboxCheckQueryOptions() {
  return queryOptions({
    queryKey: notificationKeys.hasUnread(),
    queryFn: notificationHasUnread,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
  });
}

/** Mutation options for marking specific notifications as read. */
export function notificationMarkReadMutationOptions() {
  return {
    mutationFn: notificationMarkRead,
  };
}

/** Query options for paginated full notification list. */
export function notificationListQueryOptions(params?: {
  pageSize?: number;
}) {
  return queryOptions({
    queryKey: notificationKeys.inbox(),
    queryFn: () => notificationInbox(params),
    staleTime: 1000 * 30,
  });
}

/** Mutation options for marking all notifications as read. */
export function notificationMarkAllReadMutationOptions() {
  return {
    mutationFn: notificationMarkAllRead,
  };
}